import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface SpeechToTextProps {
  onTranscription: (text: string) => void;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  append?: boolean; // If true, append to existing text; if false, replace
}

export function SpeechToText({ 
  onTranscription, 
  className, 
  size = 'sm',
  append = true 
}: SpeechToTextProps) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(() => {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  });
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isActiveRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const createRecognition = useCallback(() => {
    if (!isSupported) return null;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;  // Keep listening
    recognition.interimResults = true;  // Show interim results
    recognition.lang = 'de-DE';
    recognition.maxAlternatives = 1;
    
    recognition.onstart = () => {
      logger.log('Speech recognition started');
      setIsListening(true);
      isActiveRef.current = true;
      
      // Auto-stop after 30 seconds if no final result
      timeoutRef.current = setTimeout(() => {
        if (recognitionRef.current && isActiveRef.current) {
          logger.log('Auto-stopping speech recognition after timeout');
          recognitionRef.current.stop();
        }
      }, 30000);
    };
    
    recognition.onresult = (event) => {
      logger.log('onresult triggered, results length:', event.results.length);
      
      // Get the latest result
      const lastResultIndex = event.results.length - 1;
      const lastResult = event.results[lastResultIndex];
      
      if (lastResult && lastResult.length > 0) {
        const transcript = lastResult[0].transcript;
        const isFinal = lastResult.isFinal;
        
        logger.log('Speech recognized:', transcript, 'isFinal:', isFinal);
        
        // Only process final results to avoid duplicates
        if (isFinal) {
          // Clear timeout since we got a result
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          onTranscription(transcript);
          
          // Stop after getting final result
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        } else {
          logger.log('Interim result:', transcript);
        }
      }
    };
    
    recognition.onerror = (event) => {
      logger.log('Speech recognition error:', event.error);
      setIsListening(false);
      isActiveRef.current = false;
      
      // Only show error for serious issues
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        let errorMessage = "Fehler bei der Spracherkennung";
        switch (event.error) {
          case 'audio-capture':
            errorMessage = "Mikrofon nicht verfügbar.";
            break;
          case 'not-allowed':
            errorMessage = "Mikrofon-Berechtigung verweigert.";
            break;
          case 'network':
            errorMessage = "Netzwerkfehler.";
            break;
        }
        
        toast({
          title: "Spracherkennung Fehler",
          description: errorMessage,
          variant: "destructive",
        });
      }
    };
    
    recognition.onend = () => {
      logger.log('Speech recognition ended');
      setIsListening(false);
      isActiveRef.current = false;
      
      // Clear timeout when ending
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    
    return recognition;
  }, [isSupported, onTranscription, toast]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      toast({
        title: "Nicht unterstützt",
        description: "Spracherkennung wird in diesem Browser nicht unterstützt.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveRef.current) {
      logger.log('Speech recognition already active');
      return;
    }

    try {
      // Clean up any existing recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore cleanup errors
        }
      }

      // Create new recognition instance
      recognitionRef.current = createRecognition();
      
      if (recognitionRef.current) {
        recognitionRef.current.start();
        logger.log('Starting speech recognition...');
      }
    } catch (error) {
      logger.error('Error starting speech recognition:', error);
      setIsListening(false);
      isActiveRef.current = false;
      
      toast({
        title: "Fehler",
        description: "Spracherkennung konnte nicht gestartet werden.",
        variant: "destructive",
      });
    }
  }, [isSupported, createRecognition, toast]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isActiveRef.current) {
      try {
        recognitionRef.current.stop();
        logger.log('Stopping speech recognition...');
      } catch (error) {
        logger.error('Error stopping speech recognition:', error);
      }
    }
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setIsListening(false);
    isActiveRef.current = false;
  }, []);

  if (!isSupported) {
    return null; // Don't render anything if not supported
  }

  return (
    <Button
      type="button"
      variant={isListening ? "default" : "outline"}
      size={size}
      onClick={isListening ? stopListening : startListening}
      className={cn(
        "flex items-center gap-2 transition-all duration-200",
        isListening && "bg-red-500 hover:bg-red-600 text-white border-red-500",
        className
      )}
      disabled={false}
    >
      {isListening ? (
        <>
          <div className="relative">
            <Mic className="h-4 w-4" />
            <div className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75"></div>
          </div>
          <span className="hidden sm:inline">Stoppen</span>
        </>
      ) : (
        <>
          <Mic className="h-4 w-4" />
          <span className="hidden sm:inline">Sprechen</span>
        </>
      )}
    </Button>
  );
}

// Type declarations for Speech Recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}